import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { qtyDisplay } from "@icraftx/shared/v1";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { fetchProductLots } from "@/shared/api/inventory";
import { EmptyState } from "@/modules/shell/EmptyState";
import { PageFrame } from "@/modules/shell/PageFrame";
import { PageHeader } from "@/modules/shell/PageHeader";
import {
  queryErrorMessage,
  useAuthRedirectOnQueryError,
} from "@/shared/query/query-error";
import { queryKeys } from "@/shared/query/keys";

export function LotInquiryPage() {
  const { companySlug, productId } = useParams();

  const lotsQuery = useQuery({
    queryKey: queryKeys.products.lots(companySlug ?? "", productId ?? ""),
    enabled: Boolean(companySlug && productId),
    queryFn: () => fetchProductLots(companySlug!, productId!),
  });
  const errorMsg = queryErrorMessage(lotsQuery.error, lotsQuery.isError);
  useAuthRedirectOnQueryError(errorMsg);

  if (!companySlug || !productId) return null;

  const data = lotsQuery.data ?? null;
  const displayError =
    errorMsg && errorMsg !== "Not signed in" ? errorMsg : null;

  return (
    <PageFrame width="narrow">
      {lotsQuery.isPending || !data ? (
        <div>
          <PageHeader
            eyebrow={
              <Link
                to={`/c/${companySlug}/products/${productId}`}
                className="text-inherit no-underline hover:underline"
              >
                Product
              </Link>
            }
            title="Lots"
          />
          <div className="mt-6">
            {displayError ? (
              <Alert variant="destructive">{displayError}</Alert>
            ) : (
              <Alert variant="muted">Loading lots…</Alert>
            )}
          </div>
        </div>
      ) : (
        <>
          <PageHeader
            eyebrow={
              <Link
                to={`/c/${companySlug}/products/${productId}`}
                className="text-inherit no-underline hover:underline"
              >
                Product
              </Link>
            }
            title={data.product.name}
            description={[
              "Lot / serial inquiry",
              data.product.sku ? `SKU ${data.product.sku}` : null,
              data.product.trackLot ? "lots" : null,
              data.product.trackSerial ? "serials" : null,
            ]
              .filter(Boolean)
              .join(" · ")}
          />
          {displayError && (
            <Alert variant="destructive" className="mt-4">
              {displayError}
            </Alert>
          )}

          <h2 className="mt-8 m-0 text-sm font-semibold text-[var(--color-muted)]">
            On hand by lot / serial
          </h2>
          {data.lots.length === 0 ? (
            <div className="mt-4">
              <EmptyState
                title="No tracked balances"
                description={
                  data.product.trackLot || data.product.trackSerial
                    ? "Receive or sell with lot/serial to see balances here."
                    : "Enable lot or serial tracking on this product first."
                }
                action={
                  <Button asChild size="sm" variant="outline">
                    <Link
                      to={`/c/${companySlug}/products/${productId}`}
                      className="no-underline"
                    >
                      Back to product
                    </Link>
                  </Button>
                }
              />
            </div>
          ) : (
            <table className="mt-4 w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-[var(--color-border)] text-left text-xs uppercase tracking-wider text-[var(--color-muted)]">
                  <th className="py-2 pr-2 font-semibold">Warehouse</th>
                  <th className="py-2 pr-2 font-semibold">Lot</th>
                  <th className="py-2 pr-2 font-semibold">Serial</th>
                  <th className="py-2 text-right font-semibold">Qty</th>
                </tr>
              </thead>
              <tbody>
                {data.lots.map((r) => (
                  <tr
                    key={`${r.warehouseId}-${r.lotCode ?? ""}-${r.serialCode ?? ""}`}
                    className="border-b border-[var(--color-border)]/60"
                  >
                    <td className="py-2 pr-2 font-mono text-xs">
                      {r.warehouseCode}
                    </td>
                    <td className="py-2 pr-2 font-mono text-xs">
                      {r.lotCode ?? "—"}
                    </td>
                    <td className="py-2 pr-2 font-mono text-xs">
                      {r.serialCode ?? "—"}
                    </td>
                    <td className="py-2 text-right font-mono">
                      {qtyDisplay(r.qty)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <h2 className="mt-10 m-0 text-xs font-semibold uppercase tracking-wider text-[var(--color-muted)]">
            Recent moves
          </h2>
          {data.moves.length === 0 ? (
            <p className="mt-4 text-sm text-[var(--color-muted)]">
              No stock moves yet.
            </p>
          ) : (
            <table className="mt-4 w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-[var(--color-border)] text-left text-xs uppercase tracking-wider text-[var(--color-muted)]">
                  <th className="py-2 pr-2 font-semibold">When</th>
                  <th className="py-2 pr-2 font-semibold">Wh</th>
                  <th className="py-2 pr-2 font-semibold">Lot / serial</th>
                  <th className="py-2 pr-2 font-semibold">Reason</th>
                  <th className="py-2 text-right font-semibold">Qty</th>
                </tr>
              </thead>
              <tbody>
                {data.moves.map((m) => (
                  <tr
                    key={m.id}
                    className="border-b border-[var(--color-border)]/60 align-top"
                  >
                    <td className="py-2 pr-2 text-xs text-[var(--color-muted)]">
                      {new Date(m.createdAt).toLocaleString("en-PH", {
                        timeZone: "Asia/Manila",
                      })}
                    </td>
                    <td className="py-2 pr-2 font-mono text-xs">
                      {m.warehouseCode}
                    </td>
                    <td className="py-2 pr-2 font-mono text-xs">
                      {[m.lotCode, m.serialCode].filter(Boolean).join(" · ") ||
                        "—"}
                    </td>
                    <td className="py-2 pr-2 text-[var(--color-muted)]">
                      {m.reason}
                    </td>
                    <td className="py-2 text-right font-mono">
                      {qtyDisplay(m.qty)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </PageFrame>
  );
}
